import * as TimSort from 'timsort';

import { compareByLabel } from '../util/compare';
import { Subscription } from '../util/rxjs';

import { LocaleService } from './locale.service';
import { LocalizedModelService } from './localized-model.service';

export interface ModelFunctions<T> {
  sort: (array: T[]) => T[];
  filter: (value: T) => boolean;
  exclude: (value: T) => boolean;
}

const defaultModelFunctions: ModelFunctions<any> = {
  sort: array => {
    TimSort.sort(array, (a, b) => compareByLabel(a, b, { descending: false }));
    return array;
  },
  filter: array => array.slice(),
  exclude: array => array.slice(),
};

export class LocalizedModel<T extends { key?: any, value?: any, label?: string }> {
  get data() {
    return this.result;
  }

  get rawData() {
    return this.original;
  }

  private functions: ModelFunctions<T>;
  private updateSubscription: Subscription;

  private original: T[] | undefined;
  private result: T[] | undefined;

  constructor(localeService: LocaleService, private service: LocalizedModelService,
              functions: Partial<ModelFunctions<T>> = defaultModelFunctions) {
    this.functions = {
      sort: functions.sort || defaultModelFunctions.sort,
      exclude: functions.exclude || defaultModelFunctions.exclude,
      filter: functions.filter || defaultModelFunctions.filter
    };
    this.updateSubscription = localeService.locale$.skip(1).subscribe(() => {
      this.onUpdate(this.original);
    });
  }

  onDestroy() {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
  }

  onUpdate(data?: T[]) {
    this.original = this.translate(data);
    this.onUpdateExisting();
  }

  onUpdateExisting() {
    if (!this.original) {
      return;
    }

    this.result = this.original.filter(value => !this.functions.exclude(value) && this.functions.filter(value));
    this.result = this.functions.sort(this.result);
  }

  onSort() {
    if (this.result) {
      this.result = this.functions.sort(this.result);
    }
  }

  private translate(data: T[] | undefined): T[] | undefined {
    return data && this.service.translateAll(data);
  }
}
